import { parseISO, isValid } from 'date-fns';
import { easternDayBounds, easternExclusiveEnd, easternInclusiveStart } from 'util/dateUtils';
import { RootState } from 'app/store/rootReducer';
import { ColumnFilter, ColumnFilterMap, TransactionsTableFilters } from 'util/filtering/models';
import { Role } from 'API';
import type { LocalTransactionFilterInput, LocalIntFilterInput } from 'app/util/searchTypes';

// Option id for the "Returned" entry shown in the review-status dropdown; it
// filters the returnedStatus overlay field, not the review-status column.
export const RETURNED_STATUS_FILTER_ID = 'RETURNED';

// Option id for the "New" entry shown in the review-status dropdown; it filters
// the uniform workflowStatus column, not the per-type review-status column.
export const NEW_STATUS_FILTER_ID = 'NEW';

/**
 * Merges new column filters with existing ones, preserving user-selected state
 * (selectedValues, active, rule) from the existing filters when present.
 *
 * @param existingFilters - Currently stored filters (may contain user selections)
 * @param newFilters - Freshly built filters with updated options
 * @returns Merged ColumnFilterMap
 */
export const mergeColumnFilters = (
  existingFilters: ColumnFilterMap,
  newFilters: ColumnFilterMap
): ColumnFilterMap =>
  Object.keys(newFilters).reduce((acc, key) => {
    const newFilter = newFilters[key];
    const existingFilter = existingFilters[key];
    acc[key] = {
      ...newFilter,
      selectedValues: existingFilter?.selectedValues || newFilter.selectedValues,
      active: existingFilter?.active ?? newFilter.active,
      rule: existingFilter?.rule || newFilter.rule,
    };
    return acc;
  }, {} as ColumnFilterMap);

/**
 * Checks if any column filters are currently active.
 *
 * @param columnFilters - Map of column filters to check
 * @returns true if any filter is active, false otherwise
 */
export const hasActiveFilters = (columnFilters?: ColumnFilterMap): boolean =>
  !!columnFilters && Object.values(columnFilters).some((filter) => filter.active === true);

/**
 * Clears all filter values while preserving the filter structure.
 * Resets selectedValues to empty arrays, numeric bounds to undefined (for number filters only), and sets active to false.
 *
 * @param columnFilters - Map of column filters to clear
 * @returns New ColumnFilterMap with cleared values but preserved structure
 */
export const clearColumnFilterValues = (columnFilters: ColumnFilterMap): ColumnFilterMap => {
  return Object.fromEntries(
    Object.entries(columnFilters).map(([key, filter]) => {
      const clearedFilter: ColumnFilter = {
        ...filter,
        selectedValues: [],
        active: false,
      };

      // Only add number properties for number-type filters
      if (filter.type === 'number') {
        clearedFilter.minNumber = undefined;
        clearedFilter.maxNumber = undefined;
      }

      return [key, clearedFilter];
    })
  ) as ColumnFilterMap;
};

/**
 * Extracts all statement period date ranges from the columnFilters.
 * Returns an array of date ranges (one per selected period), or null if none selected.
 */
const getStatementPeriodDateRanges = (
  columnFilters: ColumnFilterMap
): Array<{ startDate: Date; endDate: Date }> | null => {
  const statementPeriodFilter = columnFilters?.statementPeriod;
  if (!statementPeriodFilter?.active || !statementPeriodFilter.selectedValues?.length) {
    return null;
  }

  const dateRanges: Array<{ startDate: Date; endDate: Date }> = [];

  for (const selectedValue of statementPeriodFilter.selectedValues) {
    if (!selectedValue.relatedIds?.length) continue;

    const [startDateStr, endDateStr] = selectedValue.relatedIds;
    if (!startDateStr || !endDateStr) continue;

    const startDate = parseISO(startDateStr);
    const endDate = parseISO(endDateStr);

    // Skip if either date is invalid (parseISO returns Invalid Date for bad input)
    if (!isValid(startDate) || !isValid(endDate)) {
      continue;
    }

    dateRanges.push({ startDate, endDate });
  }

  return dateRanges.length > 0 ? dateRanges : null;
};

// Backend-understood filter fields the search Lambda reads directly but which
// aren't part of LocalTransactionFilterInput.
// Typed here so we can set them without an `any` escape hatch.
interface BackendExtraFilterFields {
  excludeApproved?: { eq: boolean };
  statementPeriodDateRanges?: { gt: string; lt: string }[];
  codingType?: { eq: string };
  waitingOnMe?: { eq: boolean };
}

export const generateTransactionFilterObject = (
  {
    firstDate,
    lastDate,
    extraFilters,
    columnFilters,
    onlyMyTransactions,
    codingType,
    waitingOnMe,
  }: TransactionsTableFilters,
  paidolId: string,
  role: Role,
  highnotePaymentCardIds?: Array<string>
) => {
  const statementPeriodDateRanges = getStatementPeriodDateRanges(columnFilters);

  // SPE-3541: scope personal ("My Transactions") views to the caller's own
  // cards here, by construction, so every caller is scoped identically — not
  // just the ones going through getFilterForPage. The column-filter and
  // clear-filter thunks build their filter directly via this function; if the
  // derivation lived only in getFilterForPage, filtering/clearing on a personal
  // tab would query the whole paidol unscoped (the leak this PR fixes). The
  // live card list always wins over any stale extraFilters.or.
  const scopedExtraFilters: LocalTransactionFilterInput | undefined = onlyMyTransactions
    ? { ...extraFilters, or: buildOwnCardsCondition(highnotePaymentCardIds) }
    : extraFilters;

  const filterObject = initializeTransactionFilter(paidolId, firstDate, lastDate, scopedExtraFilters);

  const extendedFilter = filterObject as LocalTransactionFilterInput & BackendExtraFilterFields;

  if (statementPeriodDateRanges && statementPeriodDateRanges.length > 0) {
    extendedFilter.excludeApproved = { eq: true };

    extendedFilter.statementPeriodDateRanges = statementPeriodDateRanges.map((range) => ({
      gt: easternInclusiveStart(range.startDate),
      lt: easternExclusiveEnd(range.endDate),
    }));
  }

  // SEE-974: table "Transaction type" quick filter. Applied as a top-level
  // condition (like excludeApproved) — the backend translates it to coding-id
  // existence clauses. 'ALL' / empty = no filter.
  if (codingType && codingType !== 'ALL') {
    extendedFilter.codingType = { eq: codingType };
  }

  if (waitingOnMe) {
    extendedFilter.waitingOnMe = { eq: true };
  }

  const orConditions = gatherOrConditions(columnFilters);

  if (orConditions.length) {
    if (!filterObject.and) filterObject.and = [];
    filterObject.and.push({ or: orConditions });
  } else if (!filterObject.and || filterObject.and.length === 0) {
    delete filterObject.and;
  }

  return filterObject;
};

export const initializeTransactionFilter = (
  paidolId: string,
  firstDate?: Date,
  lastDate?: Date,
  extraFilters?: Record<string, unknown>
): LocalTransactionFilterInput => {
  return {
    paidolId: { eq: paidolId },
    ...createDateFilter(firstDate, lastDate),
    ...extraFilters,
  };
};

/**
 * Bounds a search to whole calendar days, read in Eastern time like every date
 * the platform displays. `firstDate`/`lastDate` are the picker's calendar days
 * (or a preset's start/end instants); only their calendar date matters.
 * Inclusive of both days: [start of firstDate, start of the day after lastDate).
 */
export const createDateFilter = (firstDate?: Date, lastDate?: Date) => {
  if (!firstDate && !lastDate) return {};

  const { gt, lt } = easternDayBounds(firstDate, lastDate);
  return { eventDate: { ...(gt ? { gt } : {}), ...(lt ? { lt } : {}) } };
};

export const gatherOrConditions = (columnFilters: Record<string, ColumnFilter>) => {
  const orConditions: Array<any> = [];

  Object.values(columnFilters).forEach((filter) => {
    if (filter.key === 'statementPeriod') {
      return;
    }
    orConditions.push(...processFilterValues(filter));
  });

  return orConditions;
};

export const processFilterValues = (filter: ColumnFilter) => {
  const conditions = [];

  filter.selectedValues?.forEach((obj) => {
    // SEE-1154: a Sage project-scoped cost-code option carries the SET of projects
    // that use its (per-project) name. The same code id is shared across projects,
    // so a variant must scope to its own projects.
    if (obj.projectIds && obj.projectIds.length > 0) {
      if (filter.rule === 'noExcept') {
        // INCLUDE: one OR element per project → code id AND that project. Each is a
        // single filter object with two fields, which @searchable ANDs. Deliberately
        // NOT a nested `and` array: @searchable's OpenSearch translation drops a
        // nested `and` inside an `or` element (the clause became a no-op → every
        // row). Multi-select stays an OR of these (code, project) pairs.
        obj.projectIds.forEach((projectId) => {
          conditions.push({
            [filter.key]: { eq: obj.id },
            agaveProjectId: { eq: projectId },
          });
        });
      } else {
        // EXCLUDE: emit ONE compound (code, project) `ne` object per project. The
        // search Lambda has explicit compound-ne handling that turns each into a
        // must_not over BOTH terms, so it excludes only docs matching this code AND
        // that project — keeping sibling variants of the same code on other projects
        // visible (e.g. excluding "Crest Variant" still shows "FiveOaks Variant").
        // A name shared by multiple projects → one compound exclusion per project =
        // exclude the code only on this variant's own projects. NOTE: the two fields
        // MUST stay in one object; splitting them into separate OR elements makes the
        // backend flatten both `ne` into a single global must_not (which would hide
        // the whole code and the whole project).
        obj.projectIds.forEach((projectId) => {
          conditions.push({
            [filter.key]: { ne: obj.id },
            agaveProjectId: { ne: projectId },
          });
        });
      }
      return;
    }

    // If this filter option has related IDs, create conditions for all of them
    // Otherwise, just use the primary ID
    const idsToInclude = obj.relatedIds && obj.relatedIds.length > 0 ? obj.relatedIds : [obj.id];

    idsToInclude.forEach((id) => {
      // "Returned" lives in the review-status dropdown but is a separate overlay
      // field, so it filters returnedStatus rather than the review-status column.
      if (
        id === RETURNED_STATUS_FILTER_ID &&
        (filter.key === 'flexCodeTransactionStatus' || filter.key === 'agaveTransactionStatus')
      ) {
        conditions.push({ returnedStatus: { eq: RETURNED_STATUS_FILTER_ID } });
        return;
      }
      // "New" lives in the review-status dropdown but is the uniform workflowStatus
      // column, not the per-type review-status column.
      if (
        id === NEW_STATUS_FILTER_ID &&
        (filter.key === 'flexCodeTransactionStatus' || filter.key === 'agaveTransactionStatus')
      ) {
        conditions.push({ workflowStatus: { [filter.rule === 'noExcept' ? 'eq' : 'ne']: id } });
        return;
      }
      conditions.push({ [filter.key]: { [filter.rule === 'noExcept' ? 'eq' : 'ne']: id } });
    });
  });

  if (filter.type === 'number' && amountBoundCount(filter) > 0)
    conditions.push({ [filter.key]: processNumberTypeFilter(filter) });

  return conditions;
};

/** How many bounds a number filter has. SEE-1897: $0 is a bound ("-$500 to $0"), not a missing one. */
export const amountBoundCount = (filter: Pick<ColumnFilter, 'minNumber' | 'maxNumber'>): number =>
  [filter.minNumber, filter.maxNumber].filter((n) => n != null).length;

export const processNumberTypeFilter = (filter: ColumnFilter, noVal?: number): LocalIntFilterInput => {
  const [minNumber, maxNumber] = [filter.minNumber, filter.maxNumber].map((n) =>
    n == null ? noVal : Math.round(n)
  );

  switch (filter.rule) {
    case 'range':
      return { gte: minNumber, lte: maxNumber };
    case 'moreThan':
      return { gte: minNumber };
    case 'lessThan':
      return { lte: maxNumber };
    default:
      return {};
  }
};

// SPE-3541: sentinel paymentCardId used to force an empty result set when a
// personal ("My Transactions") view has no cards to scope to. Real Highnote
// card ids are `cd_<hex>`, so this can never match — a cardless user sees no
// rows rather than the entire paidol's transactions (the prior leak).
export const NO_OWNED_CARDS_SENTINEL = '__no_owned_cards__';

// Builds the `or` clause that restricts a transaction search to the caller's
// own payment cards. An empty card set yields a single non-matching condition
// (see NO_OWNED_CARDS_SENTINEL) so the result is empty, never unscoped.
export const buildOwnCardsCondition = (
  highnotePaymentCardIds: Array<string> | undefined
): Array<{ paymentCardId: { eq: string } }> => {
  const cardIds = highnotePaymentCardIds ?? [];
  return cardIds.length
    ? cardIds.map((id) => ({ paymentCardId: { eq: id } }))
    : [{ paymentCardId: { eq: NO_OWNED_CARDS_SENTINEL } }];
};

export const getFilterForPage = (state: RootState, pageName: string) => {
  const searchAndFilter = state.searchAndFilter[pageName] as TransactionsTableFilters;

  // Personal-view scoping (own-cards restriction) is derived inside
  // generateTransactionFilterObject from onlyMyTransactions + the live card
  // list, so it applies to every caller — including the column-filter / clear
  // thunks that build their filter directly — not just this one.
  return generateTransactionFilterObject(
    {
      ...searchAndFilter,
      columnFilters: searchAndFilter.columnFilters || {},
      filtering: searchAndFilter.filtering || false,
    },
    state.userCompanies.selectedPaidolId,
    state.userCompanies.userRole || Role.CARDHOLDER,
    state.userCompanies.highnotePaymentCardIds
  );
};
